/** Normalize `conversations.participant_ids` (jsonb) for client comparisons. */
export function normalizeParticipantIds(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function sameParticipantSet(raw: unknown, ids: string[]): boolean {
  const a = normalizeParticipantIds(raw).sort();
  const b = Array.from(new Set(ids.map(String).filter(Boolean))).sort();
  return a.length === b.length && a.length > 0 && a.every((id, i) => id === b[i]);
}

export type ConversationRow = {
  id: string;
  participant_ids: unknown;
  last_message?: string | null;
  created_at?: string;
  updated_at: string;
};

/** Stable key for deduping threads with the same participants. */
export function conversationParticipantKey(raw: unknown): string {
  const ids = normalizeParticipantIds(raw).sort();
  return ids.length > 0 ? ids.join('|') : '';
}

/** Keep the most recently updated row per participant set (fixes duplicate DM threads). */
export function dedupeConversationRows<T extends ConversationRow>(rows: T[]): T[] {
  const byKey = new Map<string, T>();
  for (const row of rows) {
    const key = conversationParticipantKey(row.participant_ids) || row.id;
    const prev = byKey.get(key);
    if (!prev || new Date(row.updated_at).getTime() > new Date(prev.updated_at).getTime()) {
      byKey.set(key, row);
    }
  }
  return Array.from(byKey.values()).sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
}
